import { useEffect, useMemo, useRef, useState } from "react";
import type { ImperativePanelHandle } from "react-resizable-panels";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Header from "@/components/Header";
import MainWorkspaceLayout from "@/components/workspace/MainWorkspaceLayout";
import { EntityListPanel } from "@/components/shared/EntityListPanel";
import { EntityDetailsShell } from "@/components/shared/EntityDetailsShell";
import { ModuleWorkspaceMapPanel } from "@/components/module-map/ModuleWorkspaceMapPanel";
import { SectionHelpTooltip } from "@/components/shared/SectionHelpTooltip";
import { ModuleRegistryTitle } from "@/components/shared/ModuleRegistryTitle";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { useIsMobile } from "@/hooks/use-mobile";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { workItemsApi } from "@/services/work-items.service";
import { geometriesApi, objectsApi, usersApi } from "@/services/api-v2.service";
import { useAuth } from "@/hooks/useAuthV2";
import { useToast } from "@/hooks/use-toast";
import type { Geometry } from "@/types/api-v2";
import type { WorkItem, WorkItemCreatePayload, WorkItemUpdatePayload } from "@/types/work-items";
import { Crosshair, ListFilter, Plus, Trash2 } from "lucide-react";

const HISTORY_ENTRY_TYPE_LABELS: Record<string, string> = {
  status: "Смена статуса",
  assignment: "Назначение исполнителя",
};

/** Пояснение для пользователя: как бэкенд строит точку для GET /work-items/map. */
function workItemMapHint(w: WorkItem): string {
  const pl = w.sourcePayload;
  const lonOk =
    pl != null &&
    (typeof pl.location_lon === "number" || typeof pl.location_lon === "string") &&
    (typeof pl.location_lat === "number" || typeof pl.location_lat === "string");
  if (w.geometryId != null) {
    return `Точка на карте: центроид геометрии #${w.geometryId} (если в БД у геометрии есть валидный контур).`;
  }
  if (w.objectId != null) {
    return `Точка на карте: центроид объединения геометрий объекта #${w.objectId} (если у объекта есть непустые геометрии).`;
  }
  if (lonOk) {
    return "Точка на карте: координаты из source_payload (поля location_lon и location_lat).";
  }
  return "Точки на карте не будет: укажите geometryId, или objectId с геометриями, или location_lon/lat в source_payload.";
}

function payloadLngLat(item: WorkItem): [number, number] | null {
  const p = item.sourcePayload;
  if (!p) return null;
  const lng = Number(p.location_lon ?? p.locationLon);
  const lat = Number(p.location_lat ?? p.locationLat);
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return [lng, lat];
}

function pointGeometryFromLngLat(item: WorkItem, lng: number, lat: number): Geometry {
  return {
    id: item.id,
    object_id: item.objectId ?? 0,
    name: item.title,
    geojson: {
      type: "Feature",
      properties: {},
      geometry: { type: "Point", coordinates: [lng, lat] },
    },
    type: "Point",
    style: {},
    created_at: item.createdAt,
  };
}
function formatDate(value?: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("ru-RU");
}

function geometryTypeBadge(type?: string | null): { label: string; className: string } {
  const v = String(type || "").toLowerCase();
  if (v.includes("point")) return { label: "● POINT", className: "bg-sky-100 text-sky-800" };
  if (v.includes("line")) return { label: "— LINE", className: "bg-amber-100 text-amber-800" };
  if (v.includes("polygon")) return { label: "▰ POLYGON", className: "bg-emerald-100 text-emerald-800" };
  return { label: `◦ ${(type || "N/A").toUpperCase()}`, className: "bg-muted text-muted-foreground" };
}

function historyEntryTypeLabel(value?: string | null): string {
  const v = String(value || "");
  return HISTORY_ENTRY_TYPE_LABELS[v] || v || "-";
}

function statusBadgeClass(statusCode?: string | null): string {
  const v = String(statusCode || "").toLowerCase();
  if (v === "new") return "border-blue-200 bg-blue-50 text-blue-700";
  if (v === "triage") return "border-violet-200 bg-violet-50 text-violet-700";
  if (v === "assigned") return "border-indigo-200 bg-indigo-50 text-indigo-700";
  if (v === "in_progress") return "border-amber-200 bg-amber-50 text-amber-700";
  if (v === "resolved") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (v === "closed") return "border-slate-200 bg-slate-100 text-slate-700";
  if (v === "reopened") return "border-orange-200 bg-orange-50 text-orange-700";
  if (v === "rejected") return "border-rose-200 bg-rose-50 text-rose-700";
  return "border-muted-foreground/20 bg-muted text-muted-foreground";
}

function priorityBadgeClass(priority: number): string {
  if (priority >= 5) return "border-rose-200 bg-rose-50 text-rose-700";
  if (priority === 4) return "border-orange-200 bg-orange-50 text-orange-700";
  if (priority === 3) return "border-amber-200 bg-amber-50 text-amber-700";
  if (priority === 2) return "border-sky-200 bg-sky-50 text-sky-700";
  return "border-emerald-200 bg-emerald-50 text-emerald-700";
}

export default function WorkItemsPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = useAuth();
  const [status, setStatus] = useState("all");
  const [assignedToUserId, setAssignedToUserId] = useState("");
  const [objectIdInput, setObjectIdInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [mapCollapsed, setMapCollapsed] = useState(false);
  const [mapPreviewGeometry, setMapPreviewGeometry] = useState<Geometry | null>(null);
  const [mapPreviewNonce, setMapPreviewNonce] = useState(0);
  const mapPanelRef = useRef<ImperativePanelHandle>(null);
  const isMobile = useIsMobile();
  const [selectedForBulkDelete, setSelectedForBulkDelete] = useState<Set<number>>(new Set());
  const [bulkDeleteMode, setBulkDeleteMode] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<WorkItem | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formMode, setFormMode] = useState<"create" | "edit">("create");
  const [formState, setFormState] = useState({
    kind: "incident",
    title: "",
    description: "",
    status: "new",
    priority: 3,
    objectId: "",
    geometryId: "",
    assignedToUserId: "",
  });
  const [etaFromLon, setEtaFromLon] = useState("");
  const [etaFromLat, setEtaFromLat] = useState("");
  const [etaLoading, setEtaLoading] = useState(false);
  const [etaResult, setEtaResult] = useState<{
    airDistanceM: number;
    roadDistanceM: number | null;
    roadDurationS: number | null;
    roadOk: boolean;
    roadError?: string | null;
  } | null>(null);

  const metaQuery = useQuery({
    queryKey: ["work-items", "meta"],
    queryFn: () => workItemsApi.getMeta(),
    staleTime: 60_000,
  });

  const statusOptions = useMemo(
    () => [{ value: "all", label: "Все" }, ...(metaQuery.data?.statuses ?? []).map((s) => ({ value: s.code, label: s.label }))],
    [metaQuery.data?.statuses],
  );

  const kindOptions = useMemo(
    () => (metaQuery.data?.kinds ?? []).map((k) => ({ value: k.code, label: k.label })),
    [metaQuery.data?.kinds],
  );

  const statusLabelByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const s of metaQuery.data?.statuses ?? []) m.set(s.code, s.label);
    return m;
  }, [metaQuery.data?.statuses]);

  const kindLabelByCode = useMemo(() => {
    const m = new Map<string, string>();
    for (const k of metaQuery.data?.kinds ?? []) m.set(k.code, k.label);
    return m;
  }, [metaQuery.data?.kinds]);

  const statusLabel = (value?: string | null): string => {
    const v = String(value || "");
    return statusLabelByCode.get(v) || v || "-";
  };

  const kindLabel = (value?: string | null): string => {
    const v = String(value || "");
    return kindLabelByCode.get(v) || v || "-";
  };

  const filters = useMemo(() => {
    const objectId = objectIdInput.trim().length > 0 ? Number(objectIdInput.trim()) : undefined;
    return {
      status: status === "all" ? undefined : status,
      assignedToUserId: assignedToUserId.trim() || undefined,
      objectId: objectId != null && !Number.isNaN(objectId) ? objectId : undefined,
      limit: 200,
    };
  }, [status, assignedToUserId, objectIdInput]);

  const listQuery = useQuery({
    queryKey: ["work-items", filters],
    queryFn: () => workItemsApi.list(filters),
  });
  const listItems = listQuery.data ?? [];
  const visibleItems = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return listItems;
    return listItems.filter((item) => {
      const haystack = `${item.title} ${item.description ?? ""} ${item.id}`.toLowerCase();
      return haystack.includes(q);
    });
  }, [listItems, searchTerm]);

  useEffect(() => {
    setEtaResult(null);
  }, [selectedId]);

  const selectedWorkItem = useMemo<WorkItem | null>(() => {
    if (selectedId == null) return null;
    return listQuery.data?.find((item) => item.id === selectedId) ?? null;
  }, [listQuery.data, selectedId]);

  const historyQuery = useQuery({
    queryKey: ["work-item-history", selectedId],
    queryFn: () => workItemsApi.getHistory(selectedId as number),
    enabled: selectedId != null,
  });

  const formObjectsQuery = useQuery({
    queryKey: ["work-items-form", "objects"],
    queryFn: () => objectsApi.list({ limit: 1000 }),
    staleTime: 60_000,
  });

  const formUsersQuery = useQuery({
    queryKey: ["work-items-form", "users"],
    queryFn: () => usersApi.list({ limit: 1000 }),
    staleTime: 60_000,
  });

  useEffect(() => {
    const firstKind = metaQuery.data?.kinds?.[0]?.code;
    const firstStatus = metaQuery.data?.statuses?.[0]?.code;
    if (!firstKind || !firstStatus) return;
    setFormState((prev) => ({
      ...prev,
      kind: kindLabelByCode.has(prev.kind) ? prev.kind : firstKind,
      status: statusLabelByCode.has(prev.status) ? prev.status : firstStatus,
    }));
  }, [kindLabelByCode, metaQuery.data?.kinds, metaQuery.data?.statuses, statusLabelByCode]);

  const selectedFormObjectId = useMemo(() => {
    const value = Number(formState.objectId);
    return Number.isFinite(value) && value > 0 ? value : null;
  }, [formState.objectId]);

  const formGeometriesQuery = useQuery({
    queryKey: ["work-items-form", "geometries", selectedFormObjectId],
    queryFn: () => objectsApi.getGeometries(selectedFormObjectId as number),
    enabled: selectedFormObjectId != null,
    staleTime: 30_000,
  });

  const objectOptions = useMemo<ComboboxOption[]>(() => {
    const rows = formObjectsQuery.data ?? [];
    return rows.map((obj) => ({
      value: String(obj.id),
      label: `${obj.name || "Без названия"} (#${obj.id})`,
      shortLabel: obj.name || `#${obj.id}`,
      fullLabel: `${obj.name || "Без названия"} • ID: ${obj.id}`,
    }));
  }, [formObjectsQuery.data]);

  const geometryOptions = useMemo<ComboboxOption[]>(() => {
    const rows = formGeometriesQuery.data ?? [];
    return rows.map((g) => {
      const badge = geometryTypeBadge(g.type);
      return {
      value: String(g.id),
      label: `${g.name || g.type || "Геометрия"} (#${g.id})`,
      shortLabel: g.name || `${g.type || "Геометрия"} #${g.id}`,
      fullLabel: `${g.name || "Геометрия"} • Тип: ${g.type || "—"} • ID: ${g.id}`,
      badgeLabel: badge.label,
      badgeClassName: badge.className,
    };
    });
  }, [formGeometriesQuery.data]);

  const assigneeOptions = useMemo<ComboboxOption[]>(() => {
    const rows = formUsersQuery.data ?? [];
    return rows.map((u) => {
      const title = [u.last_name, u.first_name].filter(Boolean).join(" ").trim();
      const fallback = u.username || u.email || u.id;
      const short = title || fallback;
      const details = [u.username, u.email].filter(Boolean).join(" • ");
      return {
        value: u.id,
        label: `${short}${details ? ` (${details})` : ""}`,
        shortLabel: short,
        fullLabel: `${short}${details ? ` • ${details}` : ""} • ID: ${u.id}`,
      };
    });
  }, [formUsersQuery.data]);
  const assigneeNameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const o of assigneeOptions) m.set(o.value, o.shortLabel || o.label);
    return m;
  }, [assigneeOptions]);

  const selectedBulkCount = selectedForBulkDelete.size;
  const allVisibleIds = useMemo(() => visibleItems.map((item) => item.id), [visibleItems]);
  const allVisibleSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selectedForBulkDelete.has(id));
  const someVisibleSelected = !allVisibleSelected && allVisibleIds.some((id) => selectedForBulkDelete.has(id));
  const listFiltersActive =
    status !== "all" || Boolean(assignedToUserId) || Boolean(objectIdInput);

  useEffect(() => {
    const visibleIdSet = new Set(allVisibleIds);
    setSelectedForBulkDelete((prev) => {
      const next = new Set<number>();
      for (const id of prev) {
        if (visibleIdSet.has(id)) next.add(id);
      }
      return next.size === prev.size ? prev : next;
    });
  }, [allVisibleIds]);

  useEffect(() => {
    if (!formState.geometryId) return;
    if (!selectedFormObjectId) return;
    if (!formGeometriesQuery.data) return;
    const exists = formGeometriesQuery.data.some((g) => String(g.id) === formState.geometryId);
    if (!exists) {
      setFormState((prev) => ({ ...prev, geometryId: "" }));
    }
  }, [formState.geometryId, selectedFormObjectId, formGeometriesQuery.data]);

  useEffect(() => {
    if (!isMobile) return;
    setMapCollapsed(true);
    mapPanelRef.current?.collapse();
  }, [isMobile]);

  const canCreate = hasPermission("work_items.create");
  const canEdit = hasPermission("work_items.edit");
  const canDelete = hasPermission("work_items.delete");
  const canChangeStatus = hasPermission("work_items.status");
  const canOpenEditForm = canEdit || canChangeStatus;

  const createMutation = useMutation({
    mutationFn: (payload: WorkItemCreatePayload) => workItemsApi.create(payload),
    onSuccess: async (createdItem) => {
      await queryClient.invalidateQueries({ queryKey: ["work-items"] });
      setSelectedId(createdItem.id);
      setIsFormOpen(false);
      toast({
        title: "Запись создана",
        description: `Запись #${createdItem.id} успешно создана.`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка создания",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: WorkItemUpdatePayload }) =>
      workItemsApi.update(id, payload),
    onSuccess: async (updatedItem) => {
      await queryClient.invalidateQueries({ queryKey: ["work-items"] });
      setSelectedId(updatedItem.id);
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка обновления",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const changeStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      workItemsApi.changeStatus(id, { status }),
    onSuccess: async (updatedItem) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-items"] }),
        queryClient.invalidateQueries({ queryKey: ["work-item-history", updatedItem.id] }),
      ]);
      setSelectedId(updatedItem.id);
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка смены статуса",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => workItemsApi.remove(id),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-items"] }),
        queryClient.invalidateQueries({ queryKey: ["work-item-history"] }),
      ]);
      if (selectedId === deletingId) {
        setSelectedId(null);
      }
      if (deletingId != null) {
        setSelectedForBulkDelete((prev) => {
          const next = new Set(prev);
          next.delete(deletingId);
          return next;
        });
      }
      setDeletingId(null);
      setDeleteCandidate(null);
      toast({
        title: "Запись удалена",
        description: "Элемент списка удален безвозвратно.",
      });
    },
    onError: (error: Error) => {
      setDeletingId(null);
      toast({
        title: "Ошибка удаления",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const bulkDeleteMutation = useMutation({
    mutationFn: async (ids: number[]) => {
      const results = await Promise.allSettled(ids.map((id) => workItemsApi.remove(id)));
      const deletedIds: number[] = [];
      let failed = 0;
      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          deletedIds.push(ids[index]);
        } else {
          failed += 1;
        }
      });
      return { deletedIds, failed };
    },
    onSuccess: async ({ deletedIds, failed }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["work-items"] }),
        queryClient.invalidateQueries({ queryKey: ["work-item-history"] }),
      ]);
      if (selectedId != null && deletedIds.includes(selectedId)) {
        setSelectedId(null);
      }
      setSelectedForBulkDelete(new Set());
      setBulkDeleteMode(false);
      setIsBulkDeleteDialogOpen(false);
      if (failed > 0) {
        toast({
          title: "Массовое удаление выполнено частично",
          description: `Удалено: ${deletedIds.length}, ошибок: ${failed}.`,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Выбранные записи удалены",
          description: `Удалено записей: ${deletedIds.length}.`,
        });
      }
    },
    onError: (error: Error) => {
      toast({
        title: "Ошибка массового удаления",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    const defaultKind = metaQuery.data?.kinds?.[0]?.code ?? "incident";
    const defaultStatus = metaQuery.data?.statuses?.[0]?.code ?? "new";
    setFormState({
      kind: defaultKind,
      title: "",
      description: "",
      status: defaultStatus,
      priority: 3,
      objectId: "",
      geometryId: "",
      assignedToUserId: "",
    });
  };

  const openCreateForm = () => {
    setFormMode("create");
    resetForm();
    setIsFormOpen(true);
  };

  const openEditForm = () => {
    if (!selectedWorkItem || !canOpenEditForm) return;
    setFormMode("edit");
    setFormState({
      kind: selectedWorkItem.kind,
      title: selectedWorkItem.title,
      description: selectedWorkItem.description || "",
      status: selectedWorkItem.status,
      priority: selectedWorkItem.priority,
      objectId: selectedWorkItem.objectId != null ? String(selectedWorkItem.objectId) : "",
      geometryId: selectedWorkItem.geometryId != null ? String(selectedWorkItem.geometryId) : "",
      assignedToUserId: selectedWorkItem.assignedToUserId || "",
    });
    setIsFormOpen(true);
  };

  const submitForm = async () => {
    const title = formState.title.trim();
    if (!title) {
      toast({
        title: "Заполните заголовок",
        description: "Поле title обязательно.",
        variant: "destructive",
      });
      return;
    }
    const parsedObjectId = formState.objectId.trim() ? Number(formState.objectId.trim()) : undefined;
    const parsedGeometryId = formState.geometryId.trim() ? Number(formState.geometryId.trim()) : undefined;
    const objectId = parsedObjectId != null && !Number.isNaN(parsedObjectId) ? parsedObjectId : undefined;
    const geometryId = parsedGeometryId != null && !Number.isNaN(parsedGeometryId) ? parsedGeometryId : undefined;

    if (formMode === "create") {
      await createMutation.mutateAsync({
        kind: formState.kind,
        title,
        description: formState.description.trim() || undefined,
        status: formState.status,
        priority: formState.priority,
        objectId,
        geometryId,
        assignedToUserId: formState.assignedToUserId.trim() || undefined,
        sourceSystem: "manual",
      });
      return;
    }

    if (!selectedWorkItem) return;
    if (!canEdit && !canChangeStatus) {
      toast({
        title: "Недостаточно прав",
        description: "Требуется право на редактирование записи или смену статуса.",
        variant: "destructive",
      });
      return;
    }

    const statusChanged = formState.status !== selectedWorkItem.status;

    if (statusChanged && !canChangeStatus) {
      toast({
        title: "Недостаточно прав",
        description: "Для смены статуса требуется право work_items.status.",
        variant: "destructive",
      });
      return;
    }

    const hasEditableChanges = canEdit && (
      title !== selectedWorkItem.title
      || (formState.description.trim() || "") !== (selectedWorkItem.description || "")
      || formState.priority !== selectedWorkItem.priority
      || (objectId ?? null) !== (selectedWorkItem.objectId ?? null)
      || (geometryId ?? null) !== (selectedWorkItem.geometryId ?? null)
      || (formState.assignedToUserId.trim() || null) !== (selectedWorkItem.assignedToUserId ?? null)
    );

    if (!hasEditableChanges && !statusChanged) {
      setIsFormOpen(false);
      return;
    }

    if (hasEditableChanges) {
      await updateMutation.mutateAsync({
        id: selectedWorkItem.id,
        payload: {
          title,
          description: formState.description.trim() || undefined,
          priority: formState.priority,
          objectId,
          geometryId,
          assignedToUserId: formState.assignedToUserId.trim() || undefined,
        },
      });
    }

    if (statusChanged) {
      await changeStatusMutation.mutateAsync({
        id: selectedWorkItem.id,
        status: formState.status,
      });
    }

    setIsFormOpen(false);
    toast({
      title: "Запись обновлена",
      description: `Изменения по записи #${selectedWorkItem.id} сохранены.`,
    });
  };

  const confirmDelete = async () => {
    if (!deleteCandidate) return;
    setDeletingId(deleteCandidate.id);
    await deleteMutation.mutateAsync(deleteCandidate.id);
  };

  const toggleBulkItem = (id: number, checked: boolean) => {
    setSelectedForBulkDelete((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  };

  const toggleAllVisible = (checked: boolean) => {
    setSelectedForBulkDelete((prev) => {
      const next = new Set(prev);
      if (checked) {
        allVisibleIds.forEach((id) => next.add(id));
      } else {
        allVisibleIds.forEach((id) => next.delete(id));
      }
      return next;
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedForBulkDelete);
    if (ids.length === 0) return;
    await bulkDeleteMutation.mutateAsync(ids);
  };

  const focusWorkItemOnMap = async (item: WorkItem) => {
    try {
      let geometry: Geometry | null = null;
      if (item.geometryId != null) {
        geometry = await geometriesApi.getById(item.geometryId);
      } else if (item.objectId != null) {
        const rows = await objectsApi.getGeometries(item.objectId);
        geometry = rows.find((row) => row.geojson) ?? null;
      } else {
        const ll = payloadLngLat(item);
        if (ll) geometry = pointGeometryFromLngLat(item, ll[0], ll[1]);
      }
      if (!geometry?.geojson) {
        toast({
          title: "Нет точки на карте",
          description: workItemMapHint(item),
        });
        return;
      }
      if (mapCollapsed) {
        mapPanelRef.current?.expand();
        setMapCollapsed(false);
      }
      setMapPreviewGeometry(geometry);
      setMapPreviewNonce((n) => n + 1);
    } catch (error) {
      toast({
        title: "Не удалось показать на карте",
        description: error instanceof Error ? error.message : "Ошибка загрузки геометрии",
        variant: "destructive",
      });
    }
  };

  return (
    <>
    <MainWorkspaceLayout
      header={<Header className="sticky top-0 z-50" onSearch={setSearchTerm} />}
      sidebar={
        <EntityListPanel
          title={
            <ModuleRegistryTitle title="Задачи и инциденты" helpLabel="Справка по реестру задач и инцидентов">
              <p className="font-medium">Зачем</p>
              <p>Реестр задач и инцидентов: отбор записей для карточки и карты.</p>
              <p className="font-medium mt-2">Как работать</p>
              <ul className="list-disc pl-4 space-y-1">
                <li>Поиск по заголовку; фильтр — статус, исполнитель, объект</li>
                <li>Клик по строке открывает карточку по центру</li>
                <li>«+» — диалог создания; корзина — режим массового удаления</li>
                <li>«На карте» и маркеры — в карточке / правой колонке</li>
              </ul>
            </ModuleRegistryTitle>
          }
          searchValue={searchTerm}
          onSearchChange={setSearchTerm}
          searchPlaceholder="Поиск по заголовку, описанию или №..."
          headerActions={
            bulkDeleteMode && canDelete ? (
              <>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-xs"
                  disabled={bulkDeleteMutation.isPending}
                  onClick={() => {
                    setBulkDeleteMode(false);
                    setSelectedForBulkDelete(new Set());
                  }}
                >
                  Отменить
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  className="h-7 px-2 text-xs"
                  disabled={selectedBulkCount === 0 || bulkDeleteMutation.isPending}
                  onClick={() => setIsBulkDeleteDialogOpen(true)}
                  title="Удалить выбранные"
                >
                  Удалить ({selectedBulkCount})
                </Button>
              </>
            ) : (
              <>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      size="icon"
                      variant="outline"
                      className="relative h-7 w-7"
                      title="Фильтры списка"
                      aria-label="Фильтры списка"
                    >
                      <ListFilter className="h-3.5 w-3.5" />
                      {listFiltersActive ? (
                        <span
                          className="absolute right-0.5 top-0.5 h-1.5 w-1.5 rounded-full bg-primary"
                          aria-hidden
                        />
                      ) : null}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    align="end"
                    className="z-[230] w-[min(100vw-1.5rem,24rem)] space-y-3 p-3"
                  >
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-foreground">Фильтры списка</p>
                      <SectionHelpTooltip label="Справка: задачи и инциденты" side="bottom">
                        <p className="font-medium">Зачем</p>
                        <p>
                          Реестр задач и инцидентов рядом с картой: список слева, карточка по центру,
                          точки на карте справа.
                        </p>
                        <p className="font-medium mt-2">Как работать</p>
                        <ul className="mt-1 list-disc space-y-1 pl-4">
                          <li>Фильтры — статус, исполнитель, объект; поиск — в строке выше</li>
                          <li>«+» — создать запись; корзина — массовое удаление</li>
                          <li>«На карте» показывает геометрию, контур объекта или точку из координат</li>
                          <li>Клик по маркеру выбирает ту же запись в реестре</li>
                          <li>Колонку карты можно свернуть</li>
                        </ul>
                      </SectionHelpTooltip>
                    </div>
                    <p className="text-[11px] text-muted-foreground">
                      Фильтры относятся к реестру слева, не к подложке карты.
                    </p>
                    <div className="space-y-1">
                      <Label className="text-xs">Статус</Label>
                      <Select value={status} onValueChange={setStatus}>
                        <SelectTrigger className="h-8 w-full">
                          <SelectValue placeholder="Статус" />
                        </SelectTrigger>
                        <SelectContent className="z-[250]">
                          {statusOptions.map((item) => (
                            <SelectItem key={item.value} value={item.value}>
                              {item.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Исполнитель</Label>
                      <Combobox
                        options={assigneeOptions}
                        value={assignedToUserId || undefined}
                        onValueChange={(value) => setAssignedToUserId(value ?? "")}
                        placeholder={formUsersQuery.isLoading ? "Исполнители..." : "Исполнитель"}
                        searchPlaceholder="Поиск исполнителя..."
                        emptyText={formUsersQuery.isLoading ? "Загрузка..." : "Пользователи не найдены"}
                        clearText="Все исполнители"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Объект</Label>
                      <Combobox
                        options={objectOptions}
                        value={objectIdInput || undefined}
                        onValueChange={(value) => setObjectIdInput(value ?? "")}
                        placeholder={formObjectsQuery.isLoading ? "Объекты..." : "Объект"}
                        searchPlaceholder="Поиск объекта..."
                        emptyText={formObjectsQuery.isLoading ? "Загрузка..." : "Объекты не найдены"}
                        clearText="Все объекты"
                      />
                    </div>
                  </PopoverContent>
                </Popover>
                {canDelete ? (
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    className="h-7 w-7"
                    title="Массовое удаление"
                    aria-label="Массовое удаление"
                    disabled={bulkDeleteMutation.isPending}
                    onClick={() => {
                      setBulkDeleteMode(true);
                      setSelectedForBulkDelete(new Set());
                    }}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
                {canCreate ? (
                  <Button
                    type="button"
                    size="icon"
                    className="h-7 w-7"
                    title="Создать запись"
                    aria-label="Создать запись"
                    onClick={openCreateForm}
                  >
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                ) : null}
              </>
            )
          }
          headerExtras={
            bulkDeleteMode && canDelete ? (
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-2 rounded-md border px-2 py-1">
                  <Checkbox
                    checked={allVisibleSelected ? true : someVisibleSelected ? "indeterminate" : false}
                    onCheckedChange={(checked) => toggleAllVisible(checked === true)}
                    aria-label="Выбрать все записи в текущем списке"
                    disabled={visibleItems.length === 0 || bulkDeleteMutation.isPending}
                  />
                  <span className="text-xs text-muted-foreground">Выбрать все</span>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs"
                  disabled={selectedBulkCount === 0 || bulkDeleteMutation.isPending}
                  onClick={() => setSelectedForBulkDelete(new Set())}
                >
                  Сбросить
                </Button>
              </div>
            ) : null
          }
          isLoading={listQuery.isLoading}
          isError={listQuery.isError}
          errorText={(listQuery.error as Error | null)?.message || "Не удалось загрузить записи"}
          emptyText="По заданным фильтрам данных нет."
          items={visibleItems}
          selectedId={selectedId}
          getId={(item) => item.id}
          onSelect={setSelectedId}
          renderItem={(item) => (
            <>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium truncate">{item.title}</div>
                <span className="text-xs text-muted-foreground shrink-0">#{item.id}</span>
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                <Badge variant="outline" className="text-[10px]">
                  {kindLabel(item.kind)}
                </Badge>
                <Badge variant="outline" className={`text-[10px] ${statusBadgeClass(item.status)}`}>
                  {statusLabel(item.status)}
                </Badge>
                <Badge variant="outline" className={`text-[10px] ${priorityBadgeClass(item.priority)}`}>
                  P{item.priority}
                </Badge>
              </div>
            </>
          )}
          renderSideActions={
            canDelete && bulkDeleteMode
              ? (item) => (
                  <Checkbox
                    checked={selectedForBulkDelete.has(item.id)}
                    onCheckedChange={(checked) => toggleBulkItem(item.id, checked === true)}
                    aria-label={`Выбрать запись #${item.id}`}
                    className="mr-2"
                    onClick={(e) => e.stopPropagation()}
                  />
                )
              : undefined
          }
        />
      }
      workspace={
        <div className="absolute inset-0 min-h-0 bg-background">
          <ResizablePanelGroup
            direction="horizontal"
            autoSaveId="work-items-workspace-map-split"
            className="h-full w-full"
          >
            <ResizablePanel defaultSize={58} minSize={28} className="min-h-0 min-w-0">
              <div className="h-full min-h-0 overflow-auto p-4">
                <EntityDetailsShell
                  selected={selectedWorkItem != null}
                  selectedTitle={selectedWorkItem ? `${selectedWorkItem.title} · #${selectedWorkItem.id}` : undefined}
                  emptyTitle="Выберите задачу или инцидент"
                  emptyDescription="Слева реестр. Откройте запись, чтобы увидеть сведения, историю и показать её на карте."
                  activeTab={activeTab}
                  onTabChange={setActiveTab}
                  tabs={[
                    {
                      value: "general",
                      label: "Общие сведения",
                      content: (
                        <Card>
                          <CardHeader>
                            <div className="flex flex-wrap items-center justify-between gap-2">
                              <CardTitle className="text-base">Карточка записи</CardTitle>
                              {selectedWorkItem ? (
                                <div className="flex flex-wrap items-center gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => void focusWorkItemOnMap(selectedWorkItem)}
                                  >
                                    <Crosshair className="mr-1 h-4 w-4" />
                                    На карте
                                  </Button>
                                  {canOpenEditForm ? (
                                    <Button size="sm" variant="outline" onClick={openEditForm}>
                                      Редактировать
                                    </Button>
                                  ) : null}
                                  {canDelete ? (
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      onClick={() => setDeleteCandidate(selectedWorkItem)}
                                    >
                                      <Trash2 className="mr-1 h-4 w-4" />
                                      Удалить
                                    </Button>
                                  ) : null}
                                </div>
                              ) : null}
                            </div>
                          </CardHeader>
                          <CardContent className="space-y-2 text-sm">
                            {selectedWorkItem ? (
                              <>
                                <p>
                                  <span className="text-muted-foreground">Заголовок:</span> {selectedWorkItem.title}
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Описание:</span>{" "}
                                  {selectedWorkItem.description || "-"}
                                </p>
                                <p className="flex flex-wrap items-center gap-2">
                                  <span className="text-muted-foreground">Тип:</span>
                                  <Badge variant="outline">{kindLabel(selectedWorkItem.kind)}</Badge>
                                  <span className="text-muted-foreground">Статус:</span>
                                  <Badge variant="outline" className={statusBadgeClass(selectedWorkItem.status)}>
                                    {statusLabel(selectedWorkItem.status)}
                                  </Badge>
                                  <Badge variant="outline" className={priorityBadgeClass(selectedWorkItem.priority)}>
                                    Приоритет {selectedWorkItem.priority}
                                  </Badge>
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Исполнитель:</span>{" "}
                                  {selectedWorkItem.assignedToUserId
                                    ? assigneeNameById.get(selectedWorkItem.assignedToUserId) ||
                                      selectedWorkItem.assignedToUserId
                                    : "-"}
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Объект / геометрия:</span>{" "}
                                  {selectedWorkItem.objectId || "-"} / {selectedWorkItem.geometryId || "-"}
                                </p>
                                <p className="border-l-2 border-primary/40 pl-2 text-xs text-muted-foreground">
                                  {workItemMapHint(selectedWorkItem)}
                                </p>
                                {hasPermission("routing.use") ? (
                                  <div className="mt-3 rounded-md border border-border p-3 space-y-2">
                                    <div className="flex items-center gap-1.5">
                                      <p className="text-sm font-medium">ETA по дороге</p>
                                      <SectionHelpTooltip label="Справка по ETA">
                                        <p>
                                          Расчёт времени и км от вашей точки до якоря задачи на карте
                                          (не по прямой).
                                        </p>
                                        <ul className="list-disc space-y-1 pl-4">
                                          <li>Укажите lon/lat точки «откуда» (например, база / выезд).</li>
                                          <li>«Рассчитать» вызывает OSRM через API work-items.</li>
                                        </ul>
                                      </SectionHelpTooltip>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2">
                                      <div>
                                        <Label className="text-xs">От lon</Label>
                                        <Input
                                          className="h-8"
                                          value={etaFromLon}
                                          onChange={(e) => setEtaFromLon(e.target.value)}
                                          placeholder="37.62"
                                        />
                                      </div>
                                      <div>
                                        <Label className="text-xs">От lat</Label>
                                        <Input
                                          className="h-8"
                                          value={etaFromLat}
                                          onChange={(e) => setEtaFromLat(e.target.value)}
                                          placeholder="55.75"
                                        />
                                      </div>
                                    </div>
                                    <Button
                                      type="button"
                                      size="sm"
                                      className="h-7"
                                      disabled={etaLoading}
                                      onClick={() => {
                                        const lon = Number(etaFromLon);
                                        const lat = Number(etaFromLat);
                                        if (!Number.isFinite(lon) || !Number.isFinite(lat)) {
                                          toast({
                                            title: "ETA",
                                            description: "Укажите корректные lon/lat",
                                            variant: "destructive",
                                          });
                                          return;
                                        }
                                        setEtaLoading(true);
                                        setEtaResult(null);
                                        void workItemsApi
                                          .eta(selectedWorkItem.id, { lon, lat })
                                          .then((res) => {
                                            setEtaResult({
                                              airDistanceM: res.airDistanceM,
                                              roadDistanceM: res.roadDistanceM,
                                              roadDurationS: res.roadDurationS,
                                              roadOk: res.roadOk,
                                              roadError: res.roadError,
                                            });
                                          })
                                          .catch((err: unknown) => {
                                            const msg =
                                              err instanceof Error ? err.message : "Ошибка ETA";
                                            toast({
                                              title: "ETA",
                                              description: msg,
                                              variant: "destructive",
                                            });
                                          })
                                          .finally(() => setEtaLoading(false));
                                      }}
                                    >
                                      {etaLoading ? "Расчёт…" : "Рассчитать ETA"}
                                    </Button>
                                    {etaResult ? (
                                      <div className="text-xs tabular-nums space-y-0.5">
                                        {etaResult.roadOk &&
                                        etaResult.roadDistanceM != null &&
                                        etaResult.roadDurationS != null ? (
                                          <p>
                                            По дороге: {(etaResult.roadDistanceM / 1000).toFixed(1)} км ·{" "}
                                            {Math.round(etaResult.roadDurationS / 60)} мин
                                          </p>
                                        ) : (
                                          <p className="text-destructive">
                                            {etaResult.roadError || "Дорога недоступна"}
                                          </p>
                                        )}
                                        <p className="text-muted-foreground">
                                          По воздуху: {(etaResult.airDistanceM / 1000).toFixed(1)} км
                                        </p>
                                      </div>
                                    ) : null}
                                  </div>
                                ) : null}
                                <p>
                                  <span className="text-muted-foreground">Источник:</span> {selectedWorkItem.sourceSystem}
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Создано:</span>{" "}
                                  {formatDate(selectedWorkItem.createdAt)}
                                </p>
                                <p>
                                  <span className="text-muted-foreground">Обновлено:</span>{" "}
                                  {formatDate(selectedWorkItem.updatedAt)}
                                </p>
                              </>
                            ) : null}
                          </CardContent>
                        </Card>
                      ),
                    },
                    {
                      value: "history",
                      label: "История",
                      content: (
                        <Card>
                          <CardHeader>
                            <CardTitle className="text-base">История статусов и назначений</CardTitle>
                          </CardHeader>
                          <CardContent className="space-y-3">
                            {historyQuery.isLoading ? (
                              <p className="text-sm text-muted-foreground">Загрузка истории...</p>
                            ) : null}
                            {!historyQuery.isLoading && (historyQuery.data?.length ?? 0) === 0 ? (
                              <p className="text-sm text-muted-foreground">История отсутствует.</p>
                            ) : null}
                            {historyQuery.data?.map((entry) => (
                              <div key={entry.id} className="rounded-md border border-border p-2 text-sm">
                                <p className="font-medium">{historyEntryTypeLabel(entry.entryType)}</p>
                                <p className="text-xs text-muted-foreground">
                                  {entry.entryType === "status"
                                    ? `${statusLabel(entry.fromValue)} → ${statusLabel(entry.toValue)}`
                                    : `${entry.fromValue || "-"} → ${entry.toValue || "-"}`}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {entry.changedByUserId || "system"} · {formatDate(entry.changedAt)}
                                </p>
                                {entry.comment ? <p className="mt-1 text-xs">{entry.comment}</p> : null}
                              </div>
                            ))}
                          </CardContent>
                        </Card>
                      ),
                    },
                  ]}
                />
              </div>
            </ResizablePanel>
            <ResizableHandle withHandle className="w-1.5 bg-border hover:bg-primary/30" />
            <ResizablePanel
              ref={mapPanelRef}
              defaultSize={42}
              minSize={18}
              collapsible
              collapsedSize={4}
              onCollapse={() => setMapCollapsed(true)}
              onExpand={() => setMapCollapsed(false)}
              className="min-h-0 min-w-0"
            >
              <ModuleWorkspaceMapPanel
                title="Карта (задачи и инциденты)"
                collapsed={mapCollapsed}
                onRequestCollapse={() => mapPanelRef.current?.collapse()}
                onRequestExpand={() => mapPanelRef.current?.expand()}
                workItemsVisible
                onWorkItemSelect={(id) => {
                  const loaded = listItems.some((item) => item.id === id);
                  if (!loaded) {
                    toast({
                      title: "Запись вне фильтра реестра",
                      description: `Маркер #${id} виден на карте, но не проходит текущие фильтры списка.`,
                    });
                    return;
                  }
                  setSearchTerm("");
                  setSelectedId(id);
                  setActiveTab("general");
                }}
                onMvtSelect={({ objectId, geometryId }) => {
                  if (!isFormOpen) return;
                  setFormState((prev) => ({
                    ...prev,
                    objectId: String(objectId),
                    geometryId: geometryId != null ? String(geometryId) : "",
                  }));
                }}
                previewGeometry={mapPreviewGeometry}
                previewNonce={mapPreviewNonce}
                onPreviewConsumed={() => setMapPreviewGeometry(null)}
              />
            </ResizablePanel>
          </ResizablePanelGroup>
        </div>
      }
    />


      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{formMode === "create" ? "Создать запись" : "Редактировать запись"}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div className="grid gap-2 md:grid-cols-2">
              <div className="space-y-2">
                <Label>Тип</Label>
                <Select
                  value={formState.kind}
                  disabled={formMode === "edit"}
                  onValueChange={(value) =>
                    setFormState((prev) => ({ ...prev, kind: value }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {kindOptions.map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Статус</Label>
                <Select
                  value={formState.status}
                  disabled={formMode === "edit" ? !canChangeStatus : false}
                  onValueChange={(value) => setFormState((prev) => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {statusOptions.filter((item) => item.value !== "all").map((item) => (
                      <SelectItem key={item.value} value={item.value}>
                        {item.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Заголовок</Label>
              <Input
                value={formState.title}
                disabled={formMode === "edit" && !canEdit}
                onChange={(event) => setFormState((prev) => ({ ...prev, title: event.target.value }))}
                placeholder="Короткое название инцидента/задачи"
              />
            </div>

            <div className="space-y-2">
              <Label>Описание</Label>
              <Textarea
                value={formState.description}
                disabled={formMode === "edit" && !canEdit}
                onChange={(event) => setFormState((prev) => ({ ...prev, description: event.target.value }))}
                placeholder="Подробности"
                rows={4}
              />
            </div>

            <div className="grid gap-2 md:grid-cols-3">
              <div className="space-y-2">
                <Label>Приоритет (1-5)</Label>
                <Input
                  type="number"
                  min={1}
                  max={5}
                  disabled={formMode === "edit" && !canEdit}
                  value={String(formState.priority)}
                  onChange={(event) => {
                    const value = Number(event.target.value);
                    setFormState((prev) => ({
                      ...prev,
                      priority: Number.isNaN(value) ? prev.priority : Math.min(5, Math.max(1, value)),
                    }));
                  }}
                />
              </div>
              <div className="space-y-2">
                <Label>Объект</Label>
                <Combobox
                  options={objectOptions}
                  value={formState.objectId || undefined}
                  disabled={formMode === "edit" && !canEdit}
                  onValueChange={(value) =>
                    setFormState((prev) => ({
                      ...prev,
                      objectId: value ?? "",
                      geometryId: value === prev.objectId ? prev.geometryId : "",
                    }))
                  }
                  placeholder={formObjectsQuery.isLoading ? "Загрузка объектов..." : "Выберите объект"}
                  searchPlaceholder="Поиск объекта..."
                  emptyText={formObjectsQuery.isLoading ? "Загрузка..." : "Объекты не найдены"}
                />
              </div>
              <div className="space-y-2">
                <Label>Геометрия</Label>
                <Combobox
                  options={geometryOptions}
                  value={formState.geometryId || undefined}
                  disabled={(formMode === "edit" && !canEdit) || !selectedFormObjectId}
                  onValueChange={(value) => setFormState((prev) => ({ ...prev, geometryId: value ?? "" }))}
                  placeholder={
                    !selectedFormObjectId
                      ? "Сначала выберите объект"
                      : formGeometriesQuery.isLoading
                        ? "Загрузка геометрий..."
                        : "Выберите геометрию"
                  }
                  searchPlaceholder="Поиск геометрии..."
                  emptyText={
                    !selectedFormObjectId
                      ? "Выберите объект"
                      : formGeometriesQuery.isLoading
                        ? "Загрузка..."
                        : "Геометрии не найдены"
                  }
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Исполнитель</Label>
              <Combobox
                options={assigneeOptions}
                value={formState.assignedToUserId || undefined}
                disabled={formMode === "edit" && !canEdit}
                onValueChange={(value) =>
                  setFormState((prev) => ({ ...prev, assignedToUserId: value ?? "" }))
                }
                placeholder={formUsersQuery.isLoading ? "Загрузка пользователей..." : "Выберите исполнителя"}
                searchPlaceholder="Поиск пользователя..."
                emptyText={formUsersQuery.isLoading ? "Загрузка..." : "Пользователи не найдены"}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsFormOpen(false)}>
                Отмена
              </Button>
              <Button
                onClick={() => void submitForm()}
                disabled={createMutation.isPending || updateMutation.isPending || changeStatusMutation.isPending}
              >
                {formMode === "create" ? "Создать" : "Сохранить"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteCandidate != null} onOpenChange={(open) => !open && setDeleteCandidate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить запись?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteCandidate
                ? `Запись #${deleteCandidate.id} («${deleteCandidate.title}») будет удалена безвозвратно. Это действие нельзя отменить.`
                : "Запись будет удалена безвозвратно."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteMutation.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void confirmDelete()}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? "Удаление..." : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить выбранные записи?</AlertDialogTitle>
            <AlertDialogDescription>
              Будет удалено записей: {selectedBulkCount}. Действие необратимо.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteMutation.isPending}>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => void confirmBulkDelete()}
              disabled={bulkDeleteMutation.isPending || selectedBulkCount === 0}
            >
              {bulkDeleteMutation.isPending ? "Удаление..." : `Удалить (${selectedBulkCount})`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

